import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useUIStore } from '@/state/useUIStore';
import {
  calculateDayFactor,
  calculateSunColor,
} from '@/utils/lighting';

interface SunLightingProps {
  sunPosition: THREE.Vector3;
}

/**
 * Dynamic ambient + directional lights that track the sun position.
 *
 * Ambient is kept very low and constant so that the dark hemisphere
 * remains genuinely dark. All day/sunset factors come from the shared
 * lighting utility for consistency with clouds, ocean, and terrain.
 */
export function SunLighting({ sunPosition }: SunLightingProps) {
  const dirRef = useRef<THREE.DirectionalLight>(null);
  const ambRef = useRef<THREE.AmbientLight>(null);
  const quality = useUIStore((s) => s.settings.graphicsQuality);

  useFrame((state) => {
    // Local sun height: dot(sunDir, cameraSurfaceNormal)
    // Negative on the dark side, positive on the lit side
    const camPos = state.camera.position;
    const camLen = camPos.length();
    const h = camLen > 0
      ? (sunPosition.x * camPos.x + sunPosition.y * camPos.y + sunPosition.z * camPos.z) / camLen
      : sunPosition.y;

    const day = calculateDayFactor(h);

    if (dirRef.current) {
      dirRef.current.position.set(
        sunPosition.x * 500,
        sunPosition.y * 500,
        sunPosition.z * 500,
      );
      dirRef.current.intensity = day * 1.5;
      // Warm tint at sunset
      const [sr, sg, sb] = calculateSunColor(h);
      dirRef.current.color.setRGB(
        day > 0 ? sr / day : 1.0,
        day > 0 ? sg / day : 1.0,
        day > 0 ? sb / day : 1.0,
      );
    }

    if (ambRef.current) {
      // Ambient scales with sun — night side stays truly dark
      ambRef.current.intensity = day * 0.15;
      ambRef.current.color.setRGB(0.4, 0.45, 0.55);
    }
  });

  return (
    <>
      <ambientLight ref={ambRef} />
      <directionalLight
        ref={dirRef}
        castShadow={quality === 'high'}
        shadow-mapSize={[2048, 2048]}
        shadow-camera-far={5000}
        shadow-camera-left={-2000}
        shadow-camera-right={2000}
        shadow-camera-top={2000}
        shadow-camera-bottom={-2000}
      />
    </>
  );
}
